<script setup lang="ts">
import { buttonVariants } from '@satch-ui/components/button/variants'
import FormItem from '@satch-ui/primitives/form/FormItem.vue'
import type { Filter } from '@storefront-satch/components/molecules/filter/types'
import { clsx } from 'clsx'

import { CheckboxRoot, FormControl, FormField } from '@fond-of/fondof-ui'

const props = defineProps<{
  filter: Filter
}>()

const isChecked = (haystack: string | string[], needle: string) => {
  if (!haystack) return false
  if (typeof haystack === 'string') {
    return haystack.trim().toLowerCase() === needle.trim().toLowerCase()
  }

  return haystack.includes(needle)
}
</script>

<template>
  <div class="flex flex-1 flex-row flex-wrap gap-1.5 md:gap-2">
    <FormField
      v-for="item in props.filter.values"
      v-slot="{ value, handleChange }"
      :key="item.id"
      type="checkbox"
      :unchecked-value="false"
      :value="item.id.includes('availability') ? item.name : item.label"
      :name="`[${props.filter.id}]`"
    >
      <label
        :for="`checkbox-${item.id}`"
        class="allign-middle inline-flex items-center justify-center max-md:mb-1 max-md:w-fit"
      >
        <FormItem
          :class="
            clsx([
              buttonVariants({ variant: 'secondary' }),
              'w-fit gap-1 px-2 text-sm max-md:p-1.5 md:min-w-fit',
              isChecked(
                value,
                item.id.includes('availability') ? item.name : item.label
              ) && 'bg-primary-light',
            ])
          "
        >
          <div
            v-if="props.filter.name === 'colors'"
            class="block size-6 rounded-full"
            :class="`color-item--${item?.name}`"
          />

          <FormControl>
            <CheckboxRoot
              :id="`checkbox-${item.id}`"
              :key="item.id"
              :name="`[${props.filter.id}]`"
              :value="item.id.includes('availability') ? item.name : item.label"
              :checked="
                isChecked(
                  value,
                  item.id.includes('availability') ? item.name : item.label
                )
              "
              :aria-label="item.label"
              @update:checked="handleChange"
            />
          </FormControl>
          {{ item.label }} ({{ item.count }})
        </FormItem>
      </label>
    </FormField>
  </div>
</template>

<style lang="scss">
// TODO: we should take a look at this and think about how we can tailwindify it
// since we want to use as little scss as possible
$filter-black: #383838;
$filter-blue: #008cb2;
$filter-darkblue: #183b6e;
$filter-darkgray: #777777;
$filter-gray: #909090;
$filter-yellow: #fbc612;
$filter-darkyellow: #ecc44a;
$filter-lightgray: #d5d5d5;
$filter-green: #8ead33;
$filter-lightgreen: #a3c08f;
$filter-lightblue: #6fcce4;
$filter-purple: #945387;
$filter-pink: #e090aa;
$filter-turquoise: #56c4bb;
$filter-orange: #e88b1c;
$filter-berry: #84325b;
$filter-grape: #5a2f43;
$filter-petrol: #22656c;
$filter-red: #c5332a;
$filter-olive: #574d35;
$filter-brown: #9d622a;
$filter-beige: #dbb98f;
$filter-taupe: #8a8280;
$filter-rosa: #e090aa;
$filter-white: #ededed;
$filter-silver: #dcdcdc;
$filter-gold: #af934b;
$filter-coral: #f6a79b;
$filter-mint: #8eaba0;

.color-item {
  &--black,
  &--schwarz,
  &--negro,
  &--nero,
  &--noir,
  &--sort {
    background-color: $filter-black;
  }

  &--weiss,
  &--white,
  &--blanc,
  &--hvid,
  &--blanco,
  &--bianco {
    background-color: $filter-white;
  }

  &--dunkelgruen,
  &--dark-green,
  &--dunkelgruen,
  &--mørkegrøn,
  &--verde-oscuro,
  &--vert-foncé,
  &--verde-scuro {
    background-color: $filter-green;
  }

  &--hellgruen,
  &--light-green,
  &--hellgruen,
  &--lysegrøn,
  &--verde-claro,
  &--vert-clair,
  &--verde-chiaro {
    background-color: $filter-lightgreen;
  }

  &--gruen,
  &--green,
  &--vert,
  &--grøn,
  &--verde {
    background-color: $filter-green;
  }

  &--blau,
  &--blue,
  &--bleu,
  &--blu,
  &--azul,
  &--blå {
    background-color: $filter-blue;
  }

  &--dunkelblau,
  &--darkblue,
  &--dark-blue,
  &--blu-scuro,
  &--mørkeblå,
  &--azul-oscuro,
  &--bleu-foncé {
    background-color: $filter-darkblue;
  }

  &--hellblau,
  &--lightblue,
  &--light-blue,
  &--bleu-clair,
  &--lyseblå,
  &--azul-claro,
  &--azzurro {
    background-color: $filter-lightblue;
  }

  &--grau,
  &--gray,
  &--grey,
  &--gris,
  &--grå,
  &--grigio {
    background-color: $filter-gray;
  }

  &--dunkelgrau,
  &--darkgrey,
  &--dark-grey,
  &--gris-oscuro,
  &--gris-foncé,
  &--grigio-scuro,
  &--mørkegrå {
    background-color: $filter-darkgray;
  }

  &--hellgrau,
  &--lightgrey,
  &--lysegrå,
  &--gris-claro,
  &--gris-clair,
  &--grigio-chiaro {
    background-color: $filter-lightgray;
  }

  &--gelb,
  &--yellow,
  &--jaune,
  &--gul,
  &--amarillo,
  &--jaune,
  &--giallo {
    background-color: $filter-yellow;
  }

  &--dunkelgelb,
  &--dark-yellow {
    background-color: $filter-darkyellow;
  }

  &--lila,
  &--purple,
  &--lilas,
  &--lilla,
  &--purpura,
  &--morada,
  &--morado,
  &--violet,
  &--viola {
    background-color: $filter-purple;
  }

  &--pink,
  &--fuchsia,
  &--framboise,
  &--fucsia {
    background-color: $filter-pink;
  }

  &--tuerkis,
  &--turquoise,
  &--turkis,
  &--turquesa,
  &--turchese {
    background-color: $filter-turquoise;
  }

  &--orange,
  &--naranja,
  &--arancione {
    background-color: $filter-orange;
  }

  &--beere,
  &--berry,
  &--framboise,
  &--magenta,
  &--baya,
  &--bacche,
  &--bær {
    background-color: $filter-berry;
  }

  &--rot,
  &--red,
  &--rouge,
  &--rød,
  &--rojo,
  &--rosso {
    background-color: $filter-red;
  }

  &--braun,
  &--brown,
  &--marron,
  &--marrón,
  &--marrone,
  &--brun {
    background-color: $filter-brown;
  }

  &--rosa,
  &--rose,
  &--lyserød {
    background-color: $filter-rosa;
  }

  &--bunt,
  &--colorful,
  &--colore,
  &--coloré,
  &--multicolore,
  &--vistoso,
  &--colorato,
  &--colorido,
  &--farverig {
    background: url('@fond-of/satch-icons/color-picker-colorful.svg') no-repeat
      center center;
  }

  &--olive,
  &--oliva,
  &--oliven {
    background-color: $filter-olive;
  }

  &--beige,
  &--beis {
    background-color: $filter-beige;
  }

  &--taupe,
  &--topo,
  &--tortora {
    background-color: $filter-taupe;
  }

  &--silber,
  &--silver {
    background-color: $filter-silver;
  }

  &--gold {
    background-color: $filter-gold;
  }

  &--petrol {
    background-color: $filter-petrol;
  }

  &--korall,
  &--coral,
  &--koral,
  &--coral,
  &--corail,
  &--corallo {
    background-color: $filter-coral;
  }

  &--mint,
  &--menta,
  &--menthe {
    background-color: $filter-mint;
  }
}
</style>
